/** SUNTRONIX ONE — Master Customer 360 (Level 2 entry).
 *
 *  Route: /customers/:masterClientId
 *
 *  Progressive disclosure — the master customer overview + tabs.  The
 *  Subsidiaries tab reuses the existing subsidiary matrix so we don't
 *  duplicate work.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import CustomerFlows from "@/components/oneflow/CustomerFlows";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Search, ChevronRight, Plus, Building2, Globe,
  Landmark, Users, Boxes, AlertCircle, ShieldCheck, Eye,
} from "lucide-react";
import { usePreview } from "@/context/PreviewContext";
import DataTrustTab from "@/components/app/DataTrustTab";
import { VerifyChips } from "@/components/app/VerifyChips";
import DevicesTab from "@/components/app/DevicesTab";
import { ClickToCall } from "@/components/app/ClickToCall";

const TABS = [
  { key: "overview",       label: "Overview" },
  { key: "subsidiaries",   label: "Subsidiaries" },
  { key: "devices",        label: "Devices" },
  { key: "legal_entities", label: "Legal Entities" },
  { key: "data_trust",     label: "Data Trust" },
  { key: "governance",     label: "Governance" },
  { key: "history",        label: "History" },
];

export default function MasterCustomer360() {
  const { masterClientId } = useParams();
  const navigate = useNavigate();
  const { start: startPreview } = usePreview();
  const [master, setMaster] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, d] = await Promise.all([
        api.get(`/master-clients/${masterClientId}`),
        api.get(`/master-clients/${masterClientId}/dashboard`),
      ]);
      setMaster(m.data);
      setDashboard(d.data);
    } catch (e) {
      toast.error("Could not load master customer");
    } finally { setLoading(false); }
  }, [masterClientId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !master) return <div className="text-slate-400 p-6">Loading…</div>;

  return (
    <div className="space-y-6 max-w-[1600px]" data-testid="master-customer-360">
      {/* Breadcrumb */}
      <nav className="text-[12px] text-slate-500 flex items-center gap-1" data-testid="master-breadcrumb">
        <Link to="/customers" className="hover:text-slate-800">Customers</Link>
        <ChevronRight className="h-3 w-3" strokeWidth={1.6} />
        <span className="text-slate-900 font-medium">{master.name}</span>
      </nav>

      <PageHeader
        eyebrow="Master Customer"
        title={master.name}
        context={
          <span className="flex flex-wrap items-center gap-2 text-[12px] text-slate-600">
            <span className="font-mono">{master.code || "—"}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" strokeWidth={1.6} />
              {master.country || master.country_of_incorporation || "—"}</span>
            <span>·</span>
            <span>{master.base_currency || master.currency || "USD"}</span>
            <span className={"ml-2 px-2 py-0.5 text-[11px] rounded border " +
              (master.active !== false
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-slate-50 text-slate-600 border-slate-200")}>
              {master.active !== false ? "Active" : "Inactive"}
            </span>
          </span>
        }
        actions={
          <div className="flex gap-2">
            {(master.phone || master.contact_phone) && (
              <ClickToCall
                number={master.phone || master.contact_phone}
                customerName={master.name}
                customerAccountId={masterClientId}
                variant="outline"
                testId="master-360-call"
              />
            )}
            <Button variant="outline" size="sm"
              onClick={() => startPreview(masterClientId)}
              data-testid="master-360-preview">
              <Eye className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> Preview
            </Button>
            <Button size="sm" onClick={() => navigate(`/customer/${masterClientId}/tax`)}
              data-testid="master-360-tax">
              Tax
            </Button>
            <Button variant="outline" size="sm"
              onClick={() => navigate(`/customers/${masterClientId}/contracts`)}
              data-testid="master-360-contracts">
              Contracts
            </Button>
            <Button variant="outline" size="sm"
              onClick={() => navigate(`/customers/${masterClientId}/logistics`)}
              data-testid="master-360-logistics">
              Logistics
            </Button>
          </div>
        }
        testId="master-360-header"
      />

      <VerifyChips name={master.name} vat={master.vat_number || master.eu_vat}
        domain={(master.website || "").replace(/^https?:\/\//, "").replace(/\/.*$/, "") ||
          ((master.email || "").split("@")[1] || null)}
        testid="cust-verify" />

      {/* Tab bar */}
      <div className="border-b border-[color:var(--st-border)] flex gap-1 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`master-tab-${t.key}`}
            className={"px-3 py-2 text-[12px] font-medium border-b-2 -mb-px " +
              (tab === t.key
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-700")}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <OverviewTab master={master} dashboard={dashboard} onOpenTab={setTab} />
      )}
      {tab === "subsidiaries" && (
        <SubsidiariesTab masterClientId={masterClientId} totalHint={dashboard?.customers} />
      )}
      {tab === "devices" && (
        <DevicesTab masterClientId={masterClientId} scopeLabel={master.name} />
      )}
      {tab === "legal_entities" && (
        <LegalEntitiesTab masterClientId={masterClientId} />
      )}
      {tab === "data_trust" && (
        <DataTrustTab masterClientId={masterClientId} />
      )}
      {tab === "governance" && (
        <div className="text-slate-500 text-[13px]">
          Governance rules for this master customer live in
          <Link to="/governance" className="text-[color:var(--st-accent)] hover:underline ml-1">
            /governance
          </Link>.
        </div>
      )}
      {tab === "history" && (
        <div className="text-slate-500 text-[13px]">
          Change-request history and audit trail available in
          <Link to="/audit" className="text-[color:var(--st-accent)] hover:underline ml-1">
            /audit
          </Link>.
        </div>
      )}
    </div>
  );
}

function OverviewTab({ master, dashboard, onOpenTab }) {
  const kpis = [
    { key: "subs",   label: "Subsidiaries",  value: dashboard?.customers,      icon: Building2 },
    { key: "assets", label: "Assets",        value: dashboard?.assets,         icon: Boxes },
    { key: "emp",    label: "Employees",     value: dashboard?.employees,      icon: Users },
    { key: "env",    label: "Playbooks",     value: dashboard?.tech_env_profiles, icon: ShieldCheck },
    { key: "gaps",   label: "Profile gaps",  value: (dashboard?.gaps?.no_tech_env || 0) + (dashboard?.gaps?.mdm_unknown || 0), icon: AlertCircle },
  ];
  return (
    <div className="space-y-6">
      <CustomerFlows masterClientId={master?.id} />
      <Section title="Portfolio" hint="Roll-up counts across every subsidiary." testId="overview-kpis">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {kpis.map((k) => {
            const Icon = k.icon;
            return (
              <button key={k.key}
                onClick={() => onOpenTab("subsidiaries")}
                className="st-panel p-3 text-left hover:border-slate-400 transition-colors"
                data-testid={`overview-kpi-${k.key}`}>
                <div className="text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)] flex items-center gap-1">
                  <Icon className="h-3 w-3" strokeWidth={1.6} /> {k.label}
                </div>
                <div className="text-2xl font-heading font-semibold mt-1">
                  {k.value ?? "—"}
                </div>
              </button>
            );
          })}
        </div>
      </Section>

      <ReviewsPanel masterClientId={master.id} />
      <DataTrustSummary masterClientId={master.id} onOpenTab={() => onOpenTab("data_trust")} />

      <Section title="Identity" testId="overview-identity">
        <div className="st-panel p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-[13px]">
          <IdField label="Legal Name" value={master.legal_name || master.name} />
          <IdField label="Trading Name" value={master.trading_name} />
          <IdField label="Website" value={master.website} />
          <IdField label="Master Client Code" value={master.code} />
          <IdField label="Primary Contact" value={master.primary_contact_name} />
          <IdField label="Primary Contact Email" value={master.primary_contact_email} />
          <IdField label="Country of Incorporation" value={master.country_of_incorporation || master.country} />
          <IdField label="Base Currency" value={master.base_currency || master.currency} />
        </div>
      </Section>
    </div>
  );
}

function IdField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5 text-slate-800">{value || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}

function DataTrustSummary({ masterClientId, onOpenTab }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get(`/data-trust/summary/${masterClientId}`)
      .then(({ data }) => setS(data)).catch(() => setS(null));
  }, [masterClientId]);
  if (!s) return null;
  const cards = [
    { key: "ret",  label: "Retention",     value: `${s.retention.configured} / ${s.retention.expected}`, pct: s.retention.coverage_pct },
    { key: "priv", label: "Privacy",       value: s.privacy.configured ? "Configured" : "Missing",       pct: s.privacy.configured ? 100 : 0 },
    { key: "sot",  label: "Source of Truth", value: `${s.sot.configured} / ${s.sot.expected}`,           pct: s.sot.coverage_pct },
    { key: "log",  label: "Logistics lanes", value: `${s.logistics.lanes}`,                               pct: null,
       extra: s.logistics.restricted_lanes ? `${s.logistics.restricted_lanes} restricted` : null },
  ];
  return (
    <Section title="Data Trust" hint="Configured vs missing across governance domains. Click to open the Data Trust tab." testId="data-trust-summary">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c) => (
          <button key={c.key} onClick={onOpenTab}
            className="st-panel p-3 text-left hover:border-slate-400 transition-colors"
            data-testid={`data-trust-kpi-${c.key}`}>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">{c.label}</div>
            <div className="text-xl font-heading font-semibold mt-1">{c.value}</div>
            {c.pct !== null && (
              <div className="mt-2 h-1.5 bg-slate-100 rounded overflow-hidden">
                <div className={"h-full " + (c.pct === 100 ? "bg-emerald-500"
                                   : c.pct >= 50 ? "bg-amber-500" : "bg-red-500")}
                  style={{ width: `${c.pct}%` }} />
              </div>
            )}
            {c.extra && <div className="text-[10px] text-red-700 mt-1">{c.extra}</div>}
          </button>
        ))}
      </div>
    </Section>
  );
}

function ReviewsPanel({ masterClientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/master-clients/${masterClientId}/reviews`);
      setData(r.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [masterClientId]);
  useEffect(() => { load(); }, [load]);
  if (loading) return null;
  if (!data) return null;
  const overdue = data.items.filter((s) => s.overdue);
  if (overdue.length === 0) return (
    <Section title="Section reviews" hint="Every governance section is current." testId="reviews-panel">
      <div className="st-panel p-4 flex items-center gap-2 text-emerald-700 text-[13px]">
        <ShieldCheck className="h-4 w-4" strokeWidth={2} />
        All {data.items.length} sections reviewed and current.
      </div>
    </Section>
  );
  return (
    <Section title="Section reviews"
      hint={`${overdue.length} of ${data.items.length} sections due for re-verification.`}
      testId="reviews-panel">
      <div className="st-panel divide-y divide-slate-100">
        <div className="p-3 flex items-center justify-between">
          <div className="text-[12px] text-slate-500">
            <span className="text-red-700 font-semibold">{overdue.length}</span> overdue
          </div>
          <Button size="sm" variant="outline" disabled={marking}
            data-testid="reviews-mark-all"
            onClick={async () => {
              setMarking(true);
              try {
                await api.post(`/master-clients/${masterClientId}/reviews/mark-all-current`, {});
                toast.success("All sections marked current");
                load();
              } catch { toast.error("Could not mark reviews"); }
              finally { setMarking(false); }
            }}>
            Mark all current
          </Button>
        </div>
        {overdue.map((sec) => (
          <div key={sec.section} className="p-3 flex items-center justify-between"
            data-testid={`review-section-${sec.section}`}>
            <div>
              <div className="font-medium capitalize text-[13px]">{sec.section.replace(/_/g, " ")}</div>
              <div className="text-[11px] text-slate-500">
                {sec.never_reviewed ? "Never reviewed"
                  : `Reviewed ${new Date(sec.reviewed_at).toLocaleDateString()} · Next due ${new Date(sec.next_review_at).toLocaleDateString()}`}
              </div>
            </div>
            <Button size="sm" variant="ghost"
              data-testid={`review-mark-${sec.section}`}
              onClick={async () => {
                try {
                  await api.post(`/master-clients/${masterClientId}/reviews`, {
                    section: sec.section, next_review_days: 180 });
                  toast.success(`Reviewed: ${sec.section}`);
                  load();
                } catch { toast.error("Could not save"); }
              }}>
              Mark reviewed
            </Button>
          </div>
        ))}
      </div>
    </Section>
  );
}

function SubsidiariesTab({ masterClientId, totalHint }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const PAGE = 25;

  useEffect(() => {
    setLoading(true);
    api.get(`/master-clients/${masterClientId}/customers`, {
      params: {
        q: q || undefined, country: country || undefined,
        limit: PAGE, skip,
      },
    }).then(({ data }) => {
      setRows(data.items || []);
      setTotal(data.total || 0);
    }).finally(() => setLoading(false));
  }, [masterClientId, q, country, skip]);

  const pages = Math.max(1, Math.ceil(total / PAGE));
  const page = Math.floor(skip / PAGE) + 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 relative min-w-[280px]">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" strokeWidth={1.6} />
          <Input className="pl-8" placeholder="Search subsidiary name or code…"
            value={q} onChange={(e) => { setSkip(0); setQ(e.target.value); }}
            data-testid="subsidiary-search" />
        </div>
        <Input className="w-28" placeholder="Country" value={country}
          onChange={(e) => { setSkip(0); setCountry(e.target.value); }}
          data-testid="subsidiary-country" />
        <Button variant="create" size="sm" onClick={() =>
          navigate(`/customer/new?master_client_id=${masterClientId}`)}
          data-testid="subsidiary-add">
          <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> Add Subsidiary
        </Button>
      </div>

      <div className="st-panel overflow-x-auto" data-testid="subsidiary-table">
        <table className="w-full text-[13px]">
          <thead className="bg-[color:var(--st-surface-alt)] text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)]">
            <tr>
              <th className="text-left px-3 py-2">Subsidiary</th>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Country</th>
              <th className="text-left px-3 py-2">ABM</th>
              <th className="text-left px-3 py-2">MDM</th>
              <th className="text-left px-3 py-2">Profile</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-slate-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-400">No subsidiaries match.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id}
                className="border-t border-[color:var(--st-border)] hover:bg-[color:var(--st-surface-alt)] cursor-pointer"
                onClick={() => navigate(`/customer/${r.id}/tech-env`)}
                data-testid={`subsidiary-row-${r.code}`}>
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{r.code}</td>
                <td className="px-3 py-2">{r.country || "—"}</td>
                <td className="px-3 py-2">{r.abm || "—"}</td>
                <td className="px-3 py-2">{r.primary_mdm || "—"}</td>
                <td className="px-3 py-2 font-mono">
                  {r.profile_completeness != null ? `${r.profile_completeness}%`
                    : <span className="text-amber-600 text-[11px]">No env</span>}
                </td>
                <td className="px-3 py-2 text-slate-400"><ArrowRight className="h-4 w-4" strokeWidth={1.6} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > PAGE && (
        <div className="flex items-center justify-between text-[12px] text-slate-500"
          data-testid="subsidiary-pagination">
          <span>{total} subsidiaries · Page {page} of {pages}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline"
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - PAGE))}
              data-testid="subsidiary-prev">Previous</Button>
            <Button size="sm" variant="outline"
              disabled={skip + PAGE >= total}
              onClick={() => setSkip(skip + PAGE)}
              data-testid="subsidiary-next">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function LegalEntitiesTab({ masterClientId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/legal-entities?master_company_id=${masterClientId}`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [masterClientId]);
  if (loading) return <div className="text-slate-400 text-[13px]">Loading…</div>;
  if (!items.length) return (
    <div className="text-slate-500 text-[13px]">
      No legal entities configured. Add via
      <Link to="/admin/masters/legal_entities" className="text-[color:var(--st-accent)] hover:underline ml-1">
        Legal Entities master
      </Link>.
    </div>
  );
  return (
    <div className="st-panel overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead className="bg-[color:var(--st-surface-alt)] text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)]">
          <tr>
            <th className="text-left px-3 py-2">Legal Name</th>
            <th className="text-left px-3 py-2">Country</th>
            <th className="text-left px-3 py-2">Registration</th>
            <th className="text-left px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((le) => (
            <tr key={le.id} className="border-t border-[color:var(--st-border)]"
              data-testid={`legal-entity-${le.id}`}>
              <td className="px-3 py-2">{le.legal_name || le.name}</td>
              <td className="px-3 py-2">{le.country || "—"}</td>
              <td className="px-3 py-2 font-mono text-[11px]">{le.registration_number || "—"}</td>
              <td className="px-3 py-2">{le.active !== false ? "Active" : "Inactive"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
